"use client";

// Browser-side handle on the viewer's state. See docs/PERSISTENT_CANVAS_ARCHITECTURE.md.

import { useEffect } from "react";
import { useThree } from "@react-three/fiber";

import { debugToolsEnabled } from "@/lib/environment";

import { CONTENT_GROUP_NAME } from "@/lib/hooks/3d/use-content-bounds";
import {
  publishStageVisibilityReader,
  readSceneElements,
  readStageVisibility,
} from "@/lib/datadog/stage-visibility";
import { useViewer3DStore } from "@/lib/stores/viewer-3d";
import { usePathPlanningWorkflowStore } from "@/lib/stores/path-planning-workflow";
import { useSliceWorkflowStore } from "@/lib/stores/slice-workflow";
import { useBuildFileStore } from "@/lib/stores/buildfile";

export const SCENE_INSPECTOR_KEY = "__architect";

export const sceneInspectorEnabled = debugToolsEnabled;

export function SceneInspector(): null {
  const scene = useThree((state) => state.scene);

  useEffect(() => {
    const content = () => scene.getObjectByName(CONTENT_GROUP_NAME) ?? null;
    const stages = () => readStageVisibility(content());

    // Always published: this is what the RUM action reads at emit time, in every environment.
    publishStageVisibilityReader(stages);

    if (!sceneInspectorEnabled() || typeof window === "undefined") {
      return () => publishStageVisibilityReader(null);
    }

    Object.defineProperty(window, SCENE_INSPECTOR_KEY, {
      configurable: true,
      value: {
        scene,
        content,
        stages,
        elements: () => readSceneElements(content()),
        viewer3d: () => useViewer3DStore.getState(),
        pathPlanning: () => usePathPlanningWorkflowStore.getState(),
        sliceWorkflow: () => useSliceWorkflowStore.getState(),
        buildFile: () => useBuildFileStore.getState(),
      },
    });

    return () => {
      publishStageVisibilityReader(null);
      delete (window as unknown as Record<string, unknown>)[SCENE_INSPECTOR_KEY];
    };
  }, [scene]);

  return null;
}
